/**
 * health domain — /health/*, /cert-status, /debug/*.
 */

import type {
  RequestOptions,
  ChromiumHealthResponse,
  HealthPingResponse,
  DependenciesHealthResponse,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface CertStatusResponse {
  checkedAt: string;
  endpoints: ReadonlyArray<{
    name: string;
    url: string;
    expiresAt: string;
    daysRemaining: number;
    issuer: string;
    status: string;
  }>;
  anyExpiringWithin30Days: boolean;
}

export class HealthApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public ping(opts?: RequestOptions): Promise<HealthPingResponse> {
    return this.http.get<HealthPingResponse>('/health/ping', { ...opts });
  }

  public chromium(
    opts?: RequestOptions & { warmup?: boolean }
  ): Promise<ChromiumHealthResponse> {
    const query = opts?.warmup ? { warmup: true } : undefined;
    return this.http.get<ChromiumHealthResponse>('/health/chromium', {
      ...opts,
      ...(query ? { query } : {}),
    });
  }

  public sql(opts?: RequestOptions): Promise<DependenciesHealthResponse> {
    return this.http.get<DependenciesHealthResponse>('/health/sql', { ...opts });
  }

  public dependencies(opts?: RequestOptions): Promise<DependenciesHealthResponse> {
    return this.http.get<DependenciesHealthResponse>('/health/dependencies', { ...opts });
  }

  /**
   * Lightweight jXChange-ONLY probe (Bill 2026-06-03) for the "Core Director"
   * status badge. 200 = reachable; 503 (or network error) throws. Fast — does
   * NOT wait on the slow aggregate `dependencies()` (dataverse timeout etc.).
   */
  public coreDirector(
    opts?: RequestOptions,
  ): Promise<{ status: string; durationMs: number; error?: string | null }> {
    return this.http.get('/health/core-director', { ...opts });
  }

  /**
   * GET /health/about — what the server is actually running (.NET runtime, target framework,
   * Functions worker/SDK, OS, NuGet packages) and their support dates. Typed loosely here; the
   * shape is @retail-ops/about-panel's BackendAbout (docs/standards/about-dependencies-contract.md).
   */
  public about(opts?: RequestOptions): Promise<unknown> {
    return this.http.get<unknown>('/health/about', { ...opts });
  }

  public certStatus(opts?: RequestOptions): Promise<CertStatusResponse> {
    return this.http.get<CertStatusResponse>('/cert-status', { ...opts });
  }
}
